import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Users, Link2, Copy, Check, Trash2, ChevronRight, Loader2, Sparkles, Clock, Store, Eye,
} from "lucide-react";
import { listClients, createClient, deleteClient } from "@/lib/clientApi";
import ClientDetailDialog from "@/components/game/ClientDetailDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const LEVEL_STYLE = {
  new: { label: "Just getting started", bg: "bg-amber-50", text: "text-amber-700", dot: "#F59E0B" },
  some: { label: "Knows the basics", bg: "bg-sky-50", text: "text-sky-700", dot: "#29ABE2" },
  confident: { label: "Confident with numbers", bg: "bg-emerald-50", text: "text-emerald-700", dot: "#10B981" },
};

export default function ClientsDashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", business_name: "", business_description: "" });
  const [copiedId, setCopiedId] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    try {
      setClients(await listClients());
    } catch (e) {
      setClients([]);
    }
  };
  useEffect(() => { load(); }, []);

  const assessUrl = (token) => `${window.location.origin}/assess/${token}`;

  const copyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(assessUrl(c.assessment_token));
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = assessUrl(c.assessment_token);
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setCopiedId(c.id);
    toast.success("Assessment link copied. Send it to your client.");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const c = await createClient(form);
      setOpen(false);
      setForm({ name: "", business_name: "", business_description: "" });
      await load();
      toast.success("Client created. Copy their assessment link to get started.");
      copyLink(c);
    } catch (e) {
      toast.error("Could not create client. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await deleteClient(id);
    await load();
    toast.success("Client removed.");
  };

  return (
    <div className="relative overflow-hidden min-h-[calc(100vh-4rem)]">
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-200/40 blur-3xl floaty" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#005580]">Your clients</h1>
            <p className="text-slate-500 font-semibold mt-1">
              Create a client, send their understanding check, then tailor their learnings.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="new-client-btn"
                className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-5 py-3 bg-[#29ABE2] border-b-4 border-[#005580] active:border-b-0 active:translate-y-[3px] transition-all"
              >
                <Plus className="w-5 h-5" strokeWidth={3} /> New client
              </button>
            </DialogTrigger>
            <DialogContent data-testid="new-client-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#005580]">Add a client</DialogTitle>
                <DialogDescription>
                  We'll use these details to build their understanding check and tailor their learnings.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitCreate} className="space-y-4 mt-2">
                <Field label="Client name">
                  <input
                    data-testid="client-name-input"
                    autoFocus required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Jane Smith"
                    className="w-full rounded-xl border-2 border-slate-200 focus:border-sky-400 outline-none px-4 py-3 font-semibold text-slate-800 transition-colors"
                  />
                </Field>
                <Field label="Business name">
                  <input
                    data-testid="client-business-input"
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className="w-full rounded-xl border-2 border-slate-200 focus:border-sky-400 outline-none px-4 py-3 font-semibold text-slate-800 transition-colors"
                  />
                </Field>
                <Field label="A little about their business">
                  <textarea
                    data-testid="client-description-input"
                    rows={3} value={form.business_description}
                    onChange={(e) => setForm({ ...form, business_description: e.target.value })}
                    placeholder="What do they sell, who are their customers? We'll design the learnings and the understanding check around this."
                    className="w-full rounded-xl border-2 border-slate-200 focus:border-sky-400 outline-none px-4 py-3 font-semibold text-slate-800 transition-colors resize-none leading-relaxed"
                  />
                </Field>
                <DialogFooter>
                  <button
                    data-testid="create-client-submit"
                    type="submit" disabled={saving}
                    className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-xl py-3.5 bg-[#29ABE2] hover:bg-[#1E92C7] transition-colors disabled:opacity-70"
                  >
                    {saving ? (<><Loader2 className="w-5 h-5 animate-spin" /> Building their check…</>)
                            : (<><Sparkles className="w-5 h-5" /> Create client</>)}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {clients === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-200">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="font-display text-xl font-bold text-slate-700">No clients yet</p>
            <p className="text-slate-400 font-semibold mt-1">Add your first client to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clients.map((c, i) => {
              const completed = c.assessment_status === "completed";
              const hasLevel = !!c.knowledge_level;
              const done = hasLevel; // openable once a level is assigned
              const lvl = LEVEL_STYLE[c.knowledge_level];
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`client-card-${c.id}`}
                  className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-display font-bold text-xl text-slate-800 truncate">{c.name}</h3>
                      {c.business_name && (
                        <p className="text-sm text-slate-400 font-semibold flex items-center gap-1 truncate">
                          <Store className="w-3.5 h-3.5 shrink-0" /> {c.business_name}
                        </p>
                      )}
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-client-${c.id}`} className="text-slate-300 hover:text-rose-500 transition-colors shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display">Remove {c.name}?</AlertDialogTitle>
                          <AlertDialogDescription>This deletes their assessment result and learning progress. This can't be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(c.id)} className="bg-rose-500 hover:bg-rose-600">Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  <div className="mt-4 mb-4 flex flex-wrap items-center gap-2">
                    {hasLevel ? (
                      <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${lvl?.bg} ${lvl?.text}`}>
                        <span className="w-2 h-2 rounded-full" style={{ background: lvl?.dot }} />
                        {lvl?.label}{completed && c.assessment_score != null ? ` · ${c.assessment_score}%` : ""}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold bg-slate-100 text-slate-500">
                        <Clock className="w-3.5 h-3.5" /> Awaiting understanding check
                      </span>
                    )}
                    {hasLevel && !completed && (
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold bg-amber-50 text-amber-600">
                        <Clock className="w-3 h-3" /> check not taken yet
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`view-client-${c.id}`}
                      onClick={() => setDetailId(c.id)}
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 bg-white border-2 border-slate-200 rounded-xl px-3 py-2 hover:border-sky-300 transition-colors"
                    >
                      <Eye className="w-4 h-4" /> View & set level
                    </button>
                    <button
                      data-testid={`copy-link-${c.id}`}
                      onClick={() => copyLink(c)}
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 bg-white border-2 border-slate-200 rounded-xl px-3 py-2 hover:border-sky-300 transition-colors"
                    >
                      {copiedId === c.id ? <Check className="w-4 h-4 text-emerald-500" /> : <Link2 className="w-4 h-4" />}
                      {copiedId === c.id ? "Copied!" : "Copy check link"}
                    </button>
                    <button
                      data-testid={`open-client-${c.id}`}
                      onClick={() => navigate(`/clients/${c.id}`)}
                      disabled={!done}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-bold rounded-xl px-3 py-2 transition-all ${
                        done
                          ? "text-white bg-[#29ABE2] border-b-4 border-[#005580] active:border-b-0 active:translate-y-[3px]"
                          : "text-slate-300 bg-slate-100 cursor-not-allowed"
                      }`}
                    >
                      Open learnings <ChevronRight className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <ClientDetailDialog
        clientId={detailId}
        open={detailId != null}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        onChanged={load}
      />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider font-bold text-slate-400 mb-1.5">{label}</label>
    {children}
  </div>
);
